import * as React from 'react';
import * as Sebrae from '@sebrae/react-core';
import {
  BaSeTheme,
  BaSeContainer,
  BaSeDialogContext,
  BaSeDataTable,
  BaSeHeading5,
  BaSeBreakpointContext,
  BaSeDataList,
} from '@base/react';
import { StyledJumbotron } from '../../components/shared/jumbotron/jumbotron-styled';
import {
  DataTableWrapper,
  PaddingContainer,
} from './certificados-cliente-styled';
import {
  CERTIFICADOS_GRID_ATIVOS_ENDPOINT,
  CERTIFICADOS_GRID_ENDPOINT,
} from '../../hooks/constants';
import { Title } from '../../components/title/title';
import { DadosCertificado } from '../../components/dados-certificado/dados-certificado';
import { avaliableActions } from '../../models/actions';
import { DeleteText } from '../../components/delete/delete-text';
import {
  useCertificadosService,
  useTemplateCertificadoSasService,
} from '../../hooks/certificado';
import { Role } from '../../models/role';
import { ColumnFilter } from '../../components/data-table/column-filter';
import { ModalChangeValueCertificado } from '../../components/data-table/modal-change-certificado';

import { useCertificadosFilter } from './hooks/useCertificadosFilter';
import { useCertificadosActions } from './hooks/useCertificadosActions';
import { useCertificadosBatch } from './hooks/useCertificadosBatch';
import { CertificadoCard } from './components/CertificadoCard';
import { CertificadoCustomFilter } from './components/CertificadoCustomFilter';
import { formatDate } from './utils/certificados.utils';
import { CertificadoActionsMenu } from './components/CertificadoActionsMenu';
import { NotFound } from './components/NotFound';
import { StatusBadge } from './components/StatusBadge';
import { StatusColumnFilter } from './components/StatusColumnFilter';

const { white } = BaSeTheme.colors.defaultColors;
const { azulSebrae36, azulSebrae97, cinzaSebrae45, cinzaSebrae60 } =
  BaSeTheme.colors.institucionais;

const LOT_ROLES = [
  Role.CertificadoReprocessar,
  Role.CertificadoReprocessarErros,
  Role.Administrador,
];

// eslint-disable-next-line
export const CertificadosClientePage = () => {
  const [isSearch, setIsSearch] = React.useState(false);
  const [copyLoading, setCopyLoading] = React.useState(false);

  const { showNotification, showModal } = React.useContext(BaSeDialogContext);
  const { onMobileSmall, onMobile, onMobileLarge } = React.useContext(
    BaSeBreakpointContext,
  );

  const isMobile = onMobileSmall || onMobile || onMobileLarge;

  const {
    deleteSelectedItems,
    handleSendEmailCertificadosLote,
    handleSendWhatsappCertificadosLote,
  } = useCertificadosService();
  const { listTemplates } = useTemplateCertificadoSasService();

  const { backendPost, backendGet, backendDelete, backendPut } =
    Sebrae.useBackend();
  const { hasRoles } = Sebrae.useAuth();

  const isAdministrador = hasRoles([Role.Administrador]);

  const gridEndpoint = isAdministrador
    ? CERTIFICADOS_GRID_ENDPOINT
    : CERTIFICADOS_GRID_ATIVOS_ENDPOINT;

  const { fetchData, data, loading, error } =
    Sebrae.usePagination(gridEndpoint);

  const filter = useCertificadosFilter({ fetchData });

  const {
    sendLoading,
    setSendLoading,
    idCertificado,
    handleUpdate,
    handleSendCertificado,
    handleDeleteAction,
    handleReativarAction,
  } = useCertificadosActions({
    backendPost,
    backendPut,
    backendDelete,
    showNotification,
    fetchData,
  });

  const batch = useCertificadosBatch({
    backendGet,
    backendPut,
    showNotification,
    fetchData,
    deleteSelectedItems,
    searchText: filter.searchText,
    checkFilter: filter.checkFilter,
    listModel: filter.listModel,
    clienteCpf: filter.clienteCpf,
    setCopyLoading,
  });

  React.useEffect(() => {
    setIsSearch(data.total > 0);
  }, [data]);

  React.useEffect(() => {
    if (error) {
      showNotification({
        message: error?.message ?? 'Erro inesperado!',
        color: 'destructive',
      });
    }
  }, [error, showNotification]);

  const buildModalConfig = React.useCallback(
    (
      title,
      actions,
      content,
      { size = 'large', isFluid = true, onChoose } = {},
    ) => ({
      size,
      title,
      actions,
      isFluid,
      breakpointDelimiter: 'tablet',
      onChooseAction: onChoose,
      content: (onClose) => React.cloneElement(content, { onClose }),
    }),
    [],
  );

  const buildModalConfigBeforeClose = React.useCallback(
    (title, actions, content, size, beforeClose) => ({
      size,
      title,
      actions,
      beforeClose,
      content: (onClose) => React.cloneElement(content, { onClose }),
    }),
    [],
  );

  const showDetailsModal = React.useCallback(
    (id) =>
      buildModalConfig(
        'Dados do certificado',
        [],
        <DadosCertificado id={id} isShow />,
      ),
    [buildModalConfig],
  );

  const showEditModal = React.useCallback(
    (id) =>
      buildModalConfig(
        'Editar dados do certificado',
        [],
        <DadosCertificado
          id={id}
          notify={showNotification}
          isUpdate
          save={handleUpdate(id)}
          openModal={showModal}
        />,
      ),
    [buildModalConfig, handleUpdate, showModal, showNotification],
  );

  const showDeleteModal = React.useCallback(
    (id) => {
      const texts = [
        'Tem certeza que deseja excluir esse certificado?',
        'Ele se tornará inválido',
      ];
      return buildModalConfigBeforeClose(
        'Atenção!',
        [
          avaliableActions.confirmar,
          {
            action: avaliableActions.desistir,
            type: 'secondary',
            color: azulSebrae36,
          },
        ],
        <DeleteText texts={texts} />,
        'medium',
        (action) => handleDeleteAction(action, id),
      );
    },
    [buildModalConfigBeforeClose, handleDeleteAction],
  );

  const showDeleteBatchModal = React.useCallback(
    (isAllSelected, selectedItems, reloadData) => {
      const count = selectedItems.length || data.total;
      const plural = count > 1 ? 's' : '';
      const countLabel = selectedItems.length
        ? plural === 's'
          ? ` ${selectedItems.length}`
          : ''
        : ` ${data.total}`;

      const texts = [
        `Tem certeza que deseja excluir o${plural}${countLabel} certificado${plural}?`,
        'Não será possível recuperar.',
      ];

      return buildModalConfigBeforeClose(
        'Atenção!',
        [
          avaliableActions.confirmar,
          {
            action: avaliableActions.desistir,
            type: 'secondary',
            color: azulSebrae36,
          },
        ],
        <DeleteText texts={texts} />,
        'medium',
        (action) =>
          batch.handleDeleteSelectedItemsAction(
            action,
            selectedItems,
            isAllSelected,
            reloadData,
          ),
      );
    },
    [batch, buildModalConfigBeforeClose, data.total],
  );

  const showChangeBatchModal = React.useCallback(
    (isAllSelected, selectedItems, reloadData, isTemplate) => {
      const title = isTemplate
        ? 'Alterar template dos certificados'
        : 'Alterar nome do cliente';
      return buildModalConfig(
        title,
        [],
        <ModalChangeValueCertificado
          openModal={showModal}
          notify={showNotification}
          save={batch.handleChangeValuesCertificado(
            selectedItems,
            isAllSelected,
            reloadData,
            isTemplate,
          )}
          isChangeTemplate={isTemplate}
          listTemplates={listTemplates}
          cpf={filter.searchText}
        />,
        { size: 'medium', isFluid: false },
      );
    },
    [
      batch,
      buildModalConfig,
      filter.searchText,
      listTemplates,
      showModal,
      showNotification,
    ],
  );

  const showReativarModal = React.useCallback(
    (id) => {
      const texts = [
        'Tem certeza que deseja ativar este certificado?',
        'Ele se tornará ativo.',
      ];

      return buildModalConfigBeforeClose(
        'Atenção!',
        [
          avaliableActions.confirmarReativar,
          {
            action: avaliableActions.cancelar,
            type: 'secondary',
            color: azulSebrae36,
          },
        ],
        <DeleteText texts={texts} />,
        'medium',
        (action) => handleReativarAction(action, id),
      );
    },
    [buildModalConfigBeforeClose, handleReativarAction],
  );

  const buildRowActions = React.useCallback(
    (values, reloadData) => {
      const actions = [];

      if (values?.url) {
        actions.push({
          text: 'Visualizar certificado',
          icon: 'eye',
          color: cinzaSebrae45,
          callback: () => window.open(values.url),
        });
      }

      if (hasRoles([Role.Administrador, Role.CertificadoDetalhar])) {
        actions.push({
          text: 'Visualizar dados do certificado',
          icon: 'eye',
          color: cinzaSebrae45,
          callback: () => showModal(showDetailsModal(values.id)),
        });
      }

      if (hasRoles([Role.Administrador, Role.CertificadoAlterar])) {
        actions.push({
          text: 'Editar dados do certificado',
          icon: 'pen',
          color: cinzaSebrae45,
          callback: () => showModal(showEditModal(values.id)),
        });
      }

      if (values?.url) {
        actions.push({
          text: 'Copiar link',
          icon: 'copy',
          color: cinzaSebrae45,
          callback: () => {
            navigator.clipboard.writeText(values.url);
            showNotification({
              message: 'Dados copiados para a área de transferência!',
              color: 'confirmation',
              secondsToClose: 2,
            });
          },
        });
      }

      if (hasRoles([Role.Administrador, Role.CertificadoEnvioEmail])) {
        actions.push({
          text: 'Reenviar e-mail ao cliente',
          icon: 'envelope-upload',
          color: cinzaSebrae45,
          callback: () => handleSendCertificado(values.id, 'email'),
        });
      }

      if (hasRoles([Role.Administrador, Role.GestorNacional])) {
        actions.push({
          text: 'Reenviar whatsapp',
          icon: 'whatsapp',
          color: cinzaSebrae45,
          callback: () => handleSendCertificado(values.id, 'whatsapp'),
        });
      }

      if (hasRoles([Role.Administrador, Role.CertificadoExcluir])) {
        if (values?.ativo) {
          actions.push({
            text: 'Excluir certificado',
            icon: 'trash-alt',
            color: cinzaSebrae45,
            callback: () => showModal(showDeleteModal(values.id)),
          });
        } else if (!values?.ativo && hasRoles([Role.Administrador])) {
          actions.push({
            text: 'Reativar certificado',
            icon: 'history-alt',
            color: cinzaSebrae45,
            callback: () => showModal(showReativarModal(values.id)),
          });
        }
      }

      return actions;
    },
    [
      handleSendCertificado,
      hasRoles,
      showDeleteModal,
      showDetailsModal,
      showEditModal,
      showModal,
      showNotification,
      showReativarModal,
    ],
  );

  const renderRowActionsMenu = React.useCallback(
    (values, reloadData) => {
      const actions = buildRowActions(values, reloadData);
      const isLoadingThis = sendLoading && values.id === idCertificado;

      return (
        <CertificadoActionsMenu actions={actions} isLoading={isLoadingThis} />
      );
    },
    [buildRowActions, idCertificado, sendLoading],
  );

  const buildBatchActions = React.useCallback(
    (isAllSelected, selectedItems, reloadData) => {
      const baseProps = {
        color: cinzaSebrae60,
        type: 'tertiary',
        isLoading: sendLoading,
      };

      const actions = [
        {
          ...baseProps,
          value: 'Reenviar e-mail',
          leftIcon: 'envelope-upload',
          onClick: () =>
            batch.handleSendInLot(
              selectedItems,
              isAllSelected,
              reloadData,
              handleSendEmailCertificadosLote,
              setSendLoading,
            ),
        },
        {
          ...baseProps,
          value: 'Reenviar whatsapp',
          leftIcon: 'whatsapp',
          onClick: () =>
            batch.handleSendInLot(
              selectedItems,
              isAllSelected,
              reloadData,
              handleSendWhatsappCertificadosLote,
              setSendLoading,
            ),
        },
        {
          ...baseProps,
          value: 'Alterar template',
          leftIcon: 'image-edit',
          onClick: () =>
            showModal(
              showChangeBatchModal(
                isAllSelected,
                selectedItems,
                reloadData,
                true,
              ),
            ),
        },
        {
          ...baseProps,
          value: 'Excluir',
          leftIcon: 'trash-alt',
          onClick: () =>
            showModal(
              showDeleteBatchModal(isAllSelected, selectedItems, reloadData),
            ),
        },
        {
          ...baseProps,
          value: 'Copiar links',
          leftIcon: 'copy',
          isLoading: copyLoading,
          onClick: () =>
            batch.handleCopyAll(isAllSelected, selectedItems, reloadData),
        },
      ];

      if (filter.checkFilter?.index === 0 && filter.searchText.length > 13) {
        actions.unshift({
          ...baseProps,
          value: 'Alterar nome',
          leftIcon: 'edit-alt',
          onClick: () =>
            showModal(
              showChangeBatchModal(
                isAllSelected,
                selectedItems,
                reloadData,
                false,
              ),
            ),
        });
      }

      return actions;
    },
    [
      batch,
      copyLoading,
      filter.checkFilter,
      filter.searchText,
      handleSendEmailCertificadosLote,
      handleSendWhatsappCertificadosLote,
      sendLoading,
      setSendLoading,
      showChangeBatchModal,
      showDeleteBatchModal,
      showModal,
    ],
  );

  const tableConfig = React.useMemo(
    () => ({
      columnConfig: [
        { columnId: 'id', stickyOnCompactMode: true },
        {
          columnId: 'tipo',
          columnFilterIconProps: { shape: 'square' },
          width: 200,
          renderColumnFilter: ({
            setFilter,
            setTags,
            closeFilter,
            resetSelection,
          }) => (
            <ColumnFilter
              columnId="tipo"
              setFilter={setFilter}
              setTags={setTags}
              closeFilter={closeFilter}
              resetSelection={resetSelection}
              items={listTemplates}
              fetchData={fetchData}
              isSearchFor={
                filter.checkFilter.index >= 0 && !!filter.searchText.length
              }
              setIsClearFilter={filter.setIsClearFilter ?? (() => {})}
              setListModels={filter.setListModels}
              setIsListService={filter.setIsListService}
            />
          ),
        },
        ...(isAdministrador
          ? [
              {
                columnId: 'ativo',
                columnFilterIconProps: { shape: 'square' },
                width: 130,
                renderColumnFilter: ({
                  setFilter,
                  setTags,
                  closeFilter,
                  resetSelection,
                }) => (
                  <StatusColumnFilter
                    setFilter={setFilter}
                    setTags={setTags}
                    closeFilter={closeFilter}
                    resetSelection={resetSelection}
                    setIsClearFilter={filter.setIsClearFilter ?? (() => {})}
                    setIsListService={filter.setIsListService}
                    fetchData={fetchData}
                  />
                ),
              },
            ]
          : []),
      ],
      config: [
        { accessorKey: 'codigoProduto', header: 'Código do produto' },
        {
          accessorKey: 'nomeProduto',
          header: 'Produto',
          mapper: ({ values: { nomeProduto } }) => nomeProduto ?? '-',
        },
        { accessorKey: 'tipo', header: 'Template' },
        { accessorKey: 'cargaHoraria', header: 'Carga Horária' },
        {
          accessorKey: 'dataConclusao',
          header: 'Data da conclusão',
          mapper: ({ values: { dataConclusao } }) => formatDate(dataConclusao),
        },
        ...(isAdministrador
          ? [
              {
                accessorKey: 'ativo',
                header: 'Status',
                mapper: ({ values: { ativo } }) => (
                  <StatusBadge ativo={ativo} />
                ),
              },
            ]
          : []),
        {
          header: 'Ações',
          mapper: ({ values, reloadData }) =>
            renderRowActionsMenu(values, reloadData),
        },
      ],
      backgroundColor: white,
      customFilterIconVisible: false,
      hasPagination: true,
      hasSorting: true,
      initialPageIndex: 0,
      initialPageSize: 10,
      initialSortField: 'dataConclusao',
      initialSortType: 'DESC',
      itemsPerPage: [10, 20, 50],
      isLoading: filter.isClearFilter ? false : loading,
      items: filter.isClearFilter ? [] : data.items,
      total: filter.isClearFilter ? 0 : data.total,
    }),
    [
      data,
      fetchData,
      filter,
      isAdministrador,
      listTemplates,
      loading,
      renderRowActionsMenu,
    ],
  );

  const renderCustomFilter = ({ setFilter, setTags, resetSelection }) => (
    <CertificadoCustomFilter
      checkFilter={filter.checkFilter}
      searchText={filter.searchText}
      isMobile={isMobile}
      onCheckChange={(checked) => {
        filter.setCheckFilter(checked);
        filter.setSearchText('');
      }}
      onSearchChange={filter.setSearchText}
      onSearch={() =>
        filter.handleSearchType(
          setFilter,
          setTags,
          resetSelection,
          filter.checkFilter,
        )
      }
    />
  );

  const handleClearFilter = ({ resetSelection }) => {
    resetSelection();
    filter.handleOnClearFilter();
  };

  const resolveSelectionActions = ({
    isAllSelected,
    selectedItems,
    reloadData,
  }) => {
    const hasItems = !!selectedItems.length || isAllSelected;
    if (!hasItems || !hasRoles(LOT_ROLES)) return [];
    return buildBatchActions(isAllSelected, selectedItems, reloadData);
  };

  const noItemsMessage = !loading || filter.isClearFilter ? <NotFound /> : '';

  return (
    <div>
      {!isMobile ? (
        <StyledJumbotron backgroundColor={azulSebrae97}>
          <Title
            titulo="Certificados Sebrae"
            subtitulo="Certificados do Cliente"
            paddingContent="16px"
            paddingSubtitulo="16px"
          />
        </StyledJumbotron>
      ) : (
        <div style={{ margin: '16px 10px' }}>
          <BaSeHeading5 color={cinzaSebrae45}>
            Listagem de certificados
          </BaSeHeading5>
        </div>
      )}

      <BaSeContainer delimitedOnBreakpoint="desktop">
        <PaddingContainer>
          <DataTableWrapper
            isDesktop={!isMobile}
            isSearch={isSearch}
            noItems={data.total === 0 || filter.isClearFilter}
          >
            {!isMobile ? (
              <BaSeDataTable
                {...tableConfig}
                hasCustomFilter
                customFilterVisible
                customFilterTitle=""
                filterAreaBackgroundColor={white}
                onClearFilter={handleClearFilter}
                renderCustomFilter={renderCustomFilter}
                selectionButtonActions={resolveSelectionActions}
                fetchData={filter.isListService ? fetchData : () => {}}
                title="Certificados existentes para o CPF"
                titleColor={cinzaSebrae45}
                hasSelection={hasRoles(LOT_ROLES)}
                notFoundMessage={noItemsMessage}
              />
            ) : (
              <BaSeDataList
                fetchData={filter.isListService ? fetchData : () => {}}
                hasCustomFilter
                customFilterVisible
                customFilterTitle=""
                onClearFilter={handleClearFilter}
                renderCustomFilter={renderCustomFilter}
                total={filter.isClearFilter ? 0 : data.total}
                isLoading={filter.isClearFilter ? false : loading}
                items={filter.isClearFilter ? [] : data.items}
                initialPageIndex={0}
                initialPageSize={10}
                itemsPerPage={[10, 20, 50]}
                densityLevel="normal"
                genderOfItemDescription="male"
                pluralItemDescription="itens"
                singularItemDescription="item"
                initialSortField="dataConclusao"
                initialSortType="DESC"
                filterAreaBackgroundColor={white}
                hasSorting
                hasPagination
                compactMode
                hasHeader={false}
                renderCard={({ values, reloadData }) => (
                  <CertificadoCard
                    values={values}
                    actions={buildRowActions(values, reloadData)}
                  />
                )}
                renderNotFoundContent={() => <NotFound />}
              />
            )}
          </DataTableWrapper>
        </PaddingContainer>
      </BaSeContainer>
    </div>
  );
};
